import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
// Imported directly, not through the `services/api` barrel: the barrel loads
// `componentIcons.ts`, whose module-scope `require.context` throws under
// Jest, so any test that pulls in this hook via the barrel fails to run.
import { scansApi } from '../../services/api/scans.api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import type { RescanPayload, ScanResults, ScanScheduleResults, ScanProfile, ScanResult } from '../../app/modules/types/scan'
import { isTerminalScanStatus } from './useRuns'

/**
 * Hook for fetching paginated scans list
 * Automatically refetches when URL params change
 */
export function useScans() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.scans.list(orgId, params),
    queryFn: () => scansApi.getAll(params),
    enabled: !!orgId,
    refetchInterval: 60000, // Refetch every 60 seconds (1 minute)
  })
}

/**
 * Hook for fetching scan schedules
 */
export function useScanSchedules() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.schedule(orgId),
    queryFn: () => scansApi.getSchedules(),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching scan DAG profiles
 */
export function useScanDAG() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.scans.dag(orgId, params),
    queryFn: () => scansApi.getProfiles(params),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching scan chart data
 */
export function useScanChart() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.chart(orgId),
    queryFn: () => scansApi.getOverview(),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching scan filter options by key
 */
export function useScanFilterOptions(key: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.filterOptions(orgId, key),
    queryFn: () => scansApi.getFilterOptions(key),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!key && !!orgId,
  })
}

/**
 * Hook for fetching a single scan by ID
 */
export function useScanDetail(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.detail(orgId, id || ''),
    queryFn: ({ signal }) => scansApi.getById(id!, signal),
    enabled: !!id && !!orgId,
    // Don't retry when the scan is missing (404) or inaccessible (403) —
    // retrying can't fix those. Other errors keep the default retry behavior.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 404 || status === 403) return false
      return failureCount < 3
    },
    refetchInterval: (query) => {
      // Stop long-polling immediately on a definitive 404/403 — the id is
      // invalid/forbidden, so repeating the request just fails forever.
      const status = (query.state.error as { response?: { status?: number } })?.response?.status
      if (status === 404 || status === 403) return false
      const data = query.state.data as { status?: string } | undefined
      return isTerminalScanStatus(data?.status) ? false : 5000
    },
  })
}
export function useScanFlow(id: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.detail(orgId, id || ''),
    queryFn: () => scansApi.getById(id!),
    enabled: !!id && !!orgId,
  })
}

/**
 * Hook for fetching scan schedule calendar data for a specific date range.
 *
 * `options.enabled` is an ADDITIVE gate, folded into the pre-existing
 * `!!startDate && !!endDate && !!orgId` expression rather than replacing it —
 * `Schedule.tsx`'s pre-existing two-argument call keeps its current
 * behaviour (the option defaults to `true`, so nothing changes for it). The
 * asset-insights actions layer is the first caller that needs the extra
 * gate: this query is real network traffic behind the `asset_insights_actions`
 * flag, and a feature-gated query must gate `enabled`, not just the UI.
 */
export function useScanScheduleCalendar(startDate: string, endDate: string, options?: { enabled?: boolean }) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.scans.scheduleCalendar(orgId, startDate, endDate),
    queryFn: () => scansApi.getScheduleCalendar(startDate, endDate),
    enabled: (options?.enabled ?? true) && !!startDate && !!endDate && !!orgId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

/**
 * Hook for fetching workflows for scan creation with pagination and search.
 *
 * `portType` narrows the list to workflows whose entry accepts that asset's
 * `port_type` (CreateScan.tsx passes the target's, the asset-insights
 * WorkflowPicker passes `'ip'` or `'domain'`).
 *
 * `placeholderData: keepPreviousData` keeps the previous page's rows on
 * screen while the next page loads: without it, every page change collapses
 * the list to `isPending` and the dialog resizes under the pointer.
 */
export function useWorkflowsForScan(
  page = 1,
  search = '',
  filters: { tags?: string[]; status?: string[]; entry?: string[] } = {},
  portType?: string
) {
  const orgId = useAuthStore((state) => state.orgId)
  const filterKey = JSON.stringify(filters)
  return useQuery({
    queryKey: [...queryKeys.scans.workflowsForScan(orgId, page), search, filterKey, portType ?? ''],
    queryFn: () => scansApi.getWorkflowsForScan(page, search, filters, portType),
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  })
}

/**
 * Hook for fetching all scannable assets (hosts, IPs, endpoints, files)
 */
export function useScannableAssets(page = 1, search = '', portType?: string) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: [...queryKeys.scans.scannableAssets(orgId, page), search, portType ?? ''],
    queryFn: () => scansApi.getScannableAssets(page, search, portType),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching available targets for a specific workflow
 */
export function useWorkflowAvailableTargets(workflowId: string | null, page = 1, search = '') {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: [...queryKeys.scans.workflowAvailableTargets(orgId, workflowId || ''), page, search],
    queryFn: () => scansApi.getWorkflowAvailableTargets(workflowId!, page, search),
    enabled: !!workflowId && !!orgId,
  })
}

// ============================================
// SCAN MUTATIONS
// ============================================

/**
 * Hook for creating a new scan (immediate execution)
 * @param data - `workflow` plus exactly one of `seeds` (1..500 seed strings of
 * ONE type, each naming an asset that already exists) or `file_id` (an uploaded
 * APK naming itself as the target) — see scanTarget.ts. A single-target launch
 * sends a one-element list; the scalar `seed` is no longer accepted.
 */
export function useCreateScan() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: { workflow: string; allow_inactive?: boolean } & ({ seeds: string[] } | { file_id: string })) =>
      scansApi.create(data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: queryKeys.scans.all(orgId) })
      toast.success('Scan created successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for creating a scheduled scan
 * @param data - `rate`, `start_time`, `workflow`, plus exactly one of `seeds`
 * or `file_id` — same XOR contract as `useCreateScan`, see scanTarget.ts.
 */
export function useCreateScanSchedule() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: {
      rate: string
      start_time: string | null
      workflow: string
    } & ({ seeds: string[] } | { file_id: string })) => scansApi.scheduleScan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.schedule(orgId) })
      queryClient.invalidateQueries({ queryKey: [orgId, 'scans', 'scheduleCalendar'] })
      toast.success('Schedule created successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for stopping a scheduled scan
 */
export function useStopScanSchedule() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (scheduleId: string) => scansApi.stopSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.schedule(orgId) })
      queryClient.invalidateQueries({ queryKey: [orgId, 'scans', 'scheduleCalendar'] })
      toast.success('Scan schedule stopped successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for deleting a scan
 */
export function useDeleteScan() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (scanId: string) => scansApi.delete(scanId),
    onSuccess: (_data, scanId) => {
      // Evict the deleted scan's own detail cache and cancel any in-flight
      // request for it — otherwise a lingering/late poll of the just-viewed
      // scan lands after deletion and 404s.
      queryClient.removeQueries({ queryKey: queryKeys.scans.detail(orgId, scanId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.all(orgId) })
      toast.success('Scan deleted successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for stopping a running scan
 */
export function useStopScan() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (scanId: string) => scansApi.stop(scanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.all(orgId) })
      toast.success('Scan stopped successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for updating a scan
 */
export function useUpdateScan() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      scanId,
      data,
    }: {
      scanId: string
      data: Partial<{ seed: string; workflow: string }>
    }) => scansApi.update(scanId, data as any),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.detail(orgId, variables.scanId) })
      toast.success('Scan updated successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for re-running a scan. Invalidation only: the re-scan flow owns the
 * feedback (its dialog explains every refusal) and the navigation.
 */
export function useRescan() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({ scanId, payload }: { scanId: string; payload: RescanPayload }) =>
      scansApi.rescan(scanId, payload),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: queryKeys.scans.all(orgId) })
    },
  })
}
